

import React, { useEffect, useState } from 'react'

import dayjs, { Dayjs } from 'dayjs';

import { CalendarIcon } from 'lucide-react'
import { Slider, Stack, Typography, styled, Grid, Tooltip, useTheme, Grow, InputAdornment } from '@mui/material'
import { SingleInputDateRangeField, SingleInputDateTimeRangeField, DateRangePicker, DateTimeRangePicker, PickersLayout, type DateRange  } from '@mui/x-date-pickers-pro';

import { Button } from '@repo/ui'
import { gridColumnsTotalWidthSelector } from '@mui/x-data-grid';

// §36 — the picker's chrome (shortcut presets, brand-purple range calendar, dropdown paper,
// 49px field) is shared with the analytics header's top-level date range, which cannot reuse
// THIS component (it is a drawer filter: it needs a DataGrid `column` and emits
// {field, operator, value}). Extracted rather than copied to that second call site.
import {
  DateRangeCalendarHeader,
  DateRangePickerDay,
  dateRangeDaySx,
  dateRangeDesktopPaperSx,
  dateRangeFieldSx,
  dateRangeShortcutItems,
  dateRangeShortcutsSx,
} from './dateRangeChrome';

const StyledPickersLayout = styled(PickersLayout)({
  '.MuiDateCalendar-root': {
    color: '#1565c0',
    borderRadius: '2px',
    borderWidth: '1px',
    borderColor: '#2196f3',
    border: '1px solid',
    backgroundColor: '#90caf9',
  }
})

interface DatePickerFilterProps {
  onChange?: () => void
  column?: object
  rows?: [], 
  filters?: null
}

export const DateRangePickerFilter = (props: DatePickerFilterProps) => {
  const { 
    column,
    rows = [],
    onChange = () => {}, 
    filters = [null, null],
  } = props

  const convertDate = (filters) => {
    const newValue = [null, null]
    if (filters.length > 0) {
      filters?.forEach((filter, index ) => {
        if (filter?.operator == 'onOrAfter') {
          newValue[0] = dayjs(filter.value)
        }
        if (filter?.operator == 'onOrBefore') {
          newValue[1] = dayjs(filter.value)
        }
      })
    }
    return newValue
  }
  
  const theme = useTheme()
  // get unique value options from row
  const options = rows.map(row => row[column.field])
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<DateRange<Dayjs>>(convertDate(filters));

  // useEffect(() => {
  //  convertDate(filters)
  // }, [filters])

  const CustomActionBar = () => {
    const theme = useTheme()

    return (
      // Pinned bottom-right over the calendar column. Background matches the popup card
      // (was a mismatched white[100] bar); the calendars are padded up (contentWrapper
      // paddingBottom in the layout sx) so this no longer overlaps the 30/31 rows.
      <Stack direction="row" spacing={3} justifyContent="flex-end"
        sx={{
          position: 'absolute',
          py: 4, px: 6,
          bottom: 0,
          right: 0,
          minWidth: 625,
          maxWidth: 625,
          zIndex: 1,
          background: theme.palette.background.paper,
        }}>
        <Button
          disableElevation 
          variant="secondary" 
          onClick={handleClear} 
        >
          Clear
        </Button>
        <Button 
          disableElevation 
          onClick={handleApply} 
          disabled={value[0] == null || value[1] == null}
        >
          Apply
        </Button>
      </Stack>
    )
  }


  const handleApply = () => {
    // TODO: create error state that make you select both a range in order to submit
    // TODO: Tre, submit these simulatanesouly
    onChange({'field': column?.field, 'operator': 'onOrAfter', 'value': value[0].toDate()})
    onChange({'field': column?.field, 'operator': 'onOrBefore', 'value': value[1].toDate()})
    setOpen(false)
  };

  const handleClear = () => {
    setValue([null, null])
  }

  return (
     <Stack spacing={4}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Typography variant="body2">{column?.headerName}</Typography>
        <Stack>
          {/* Always rendered (reserves the row height) so the field doesn't shift when a
              selection makes "Clear" appear — hidden, not unmounted, when empty. */}
          {(() => {
            const isSet = JSON.stringify(value) != JSON.stringify([null, null])
            return (
              <Typography
                onClick={isSet ? handleClear : undefined}
                sx={{
                  textDecoration: 'underline',
                  fontSize: 14,
                  cursor: 'pointer',
                  visibility: isSet ? 'visible' : 'hidden',
                }}>
                Clear
              </Typography>
            )
          })()}
        </Stack>
      </Stack>
      <Stack sx={{ width: '100%' }}>
        <DateRangePicker
          sx={{ width: '100%' }}
          name={`${column?.field}-datepicker`}
          onChange={(newValue) => setValue(newValue)}
          onAccept={handleApply}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          value={value}
          closeOnSelect={false}
          disableFuture
          slots={{ 
            // layout: StyledPickersLayout,
            field: SingleInputDateRangeField,
            // field: SingleInputDateTimeRangeField, TODO: ADD TIME SELECTOR
            actionBar:  CustomActionBar,
            day: DateRangePickerDay,
            calendarHeader: DateRangeCalendarHeader
            // desktopTransition: Grow,
            // mobileTransition: Grow,
          }}
          slotProps={{
            desktopPaper: { sx: dateRangeDesktopPaperSx(theme) },
            layout: {
              sx: {
                p: 4,
                borderRadius: 3,
                // Reserve room at the bottom of the calendars/shortcuts so the pinned
                // action bar doesn't overlap the last (30/31) calendar rows.
                '& .MuiPickersLayout-contentWrapper': { paddingBottom: '72px' },
                '& .MuiPickersLayout-shortcuts': { paddingBottom: '72px' },
              }
            },
            field: {
              // VERA-790: fill the filter field's full width, matching every other filter
              // input in the drawer (Search, LocationFilter's Autocomplete) instead of the
              // previous hardcoded 394px that read narrower/wider than its siblings.
              fullWidth: true,
              // dateSeparator: 'to',
              sx: {
                color: theme.palette.brand[300],
                width: '100%',
              }
            },
            day: { sx: dateRangeDaySx(theme) },
            textField: {
              placeholder: 'Select date',
              size: 'small',
              fullWidth: true,
              sx: dateRangeFieldSx(theme),
              /**
               * ── The old `TODO: DATE ICON ADORNMENT DOESNT WORK` is SOLVED, and the answer
               * is `dateRangeFieldWithIconSx` in `./dateRangeChrome`. ──
               *
               * The adornment was never the problem. On this field the visible box is the
               * inner `input` (the fieldset border is removed and the border/fill/height are
               * painted on `input`), while the root is a bare flex container with
               * `padding: 0`. A `startAdornment` is a flex SIBLING placed BEFORE that input,
               * so it rendered at x=0 — outside the border the user reads as the field — and
               * pushed the bordered box sideways. The instinct recorded in the old TODO
               * ("debating adding it as position absolute") was the right one; the reason it
               * is right is written out in full on that export.
               *
               * NOT adopted here, deliberately. This drawer filter is one of ~10 date filters
               * across the platform's tables and none of them was asked for an icon, so
               * turning it on here is a change to every one of them. Swap `dateRangeFieldSx`
               * for `dateRangeFieldWithIconSx` and add the `InputProps.startAdornment` (see
               * `AnalyticsFiltersHeader`) if that is ever wanted — the styling half is done.
               */
            },
            // TODO: how to make the shortcuts highlighted when teheir interval is selected
            shortcuts: {
              changeImportance: 'set',
              items: dateRangeShortcutItems,
              dense: false,
              sx: dateRangeShortcutsSx(theme),
            },
            actionBar: { actions: ['accept','clear'] },
          }}
        />
      </Stack>
    </Stack>
  )
}